import React from "react";
import PropTypes from "prop-types";
import { MinusCircleOutlined, PlusCircleOutlined } from "@ant-design/icons";
import { useForm, useFieldArray, Controller } from "react-hook-form";
import {
  Button,
  Collapse,
  Form,
  Input,
  Select,
  message,
  InputNumber,
  Col,
  Row,
  Layout,
  Tag,
} from "antd";
import { yupResolver } from "@hookform/resolvers/yup";
import Breadcrumb from "../../components/Breadcrumb";
import {
  useGetAllIndexes,
  useGetIndexFormats,
  useCreateIndexList,
} from "../../hooks/indexes.hooks";
import { getIndexListSchema } from "../../schema/indexes.schema";

const { Option } = Select;
const { Panel } = Collapse;

const tagProperties = [
  { label: "multiValue", value: "multiValue", color: "blue" },
  { label: "autoId", value: "autoId", color: "blue" },
  { label: "fixedValues", value: "fixedValues", color: "blue" },
];

const getTagColor = (value) => {
  const selected = tagProperties.find((el) => el.value === value);
  return selected ? selected.color : "blue";
};

export const IndexTagOptions = tagProperties.map((el) => ({
  label: el.label,
  value: el.value,
}));

export const IndexTagRenderer = (props) => {
  const { label, value, closable, onClose } = props;
  const onPreventMouseDown = (event) => {
    event.preventDefault();
    event.stopPropagation();
  };
  return (
    <Tag
      color={getTagColor(value)}
      onMouseDown={onPreventMouseDown}
      closable={closable}
      onClose={onClose}
      style={{ marginRight: 3 }}
    >
      {label}
    </Tag>
  );
};

const defaultIndexRecord = {
  name: "",
  type: undefined,
  wildcard: undefined,
  format: "",
  order: undefined,
  required: false,
  defaultIndex: false,
  editable: true,
  availableInIS: false,
  tags: [],
};

const CreateIndexes = () => {
  const { data: indexApiData } = useGetAllIndexes();
  const { data: indexFormatData } = useGetIndexFormats();

  const indexData = indexApiData ?? [];
  const dateFormats = indexFormatData?.formats ?? [];

  const {
    control,
    handleSubmit,
    watch,
    reset,
    getValues,
    setValue,
    formState: { errors },
  } = useForm({
    defaultValues: {
      formData: [{ ...defaultIndexRecord }],
    },
    resolver: yupResolver(getIndexListSchema(indexData)),
  });

  const { mutate, isLoading } = useCreateIndexList({
    onSuccess: (data) => {
      message.success("Index list created");
      reset();
    },
    onError: (error) => {
      message.error(error.response?.data?.message ?? "Server Error", 5);
    },
  });

  const { fields, remove, append } = useFieldArray({
    control,
    name: "formData",
  });

  const errorHandler = (errorset) => {
    let errorMsg = "";
    errorset.formData.forEach((error) => {
      if (error.name) {
        errorMsg = error.name.message;
      }
    });
    if (errorMsg) {
      message.error(errorMsg);
    }
  };

  const createIndexes = (data) => {
    mutate(data.formData);
  };

  // useEffect(() => {
  //   const subscription = watch((data) => {
  //     // console.log(data, "data");
  //   });
  //   return () => subscription.unsubscribe();
  // }, [watch]);

  return (
    <>
      <Layout>
        <div className="container_aa">
          <h3>Index Create</h3>
          <Breadcrumb
            routes={[
              { path: "/", breadcrumbName: "Dashboard" },
              { path: "/index/create", breadcrumbName: "Index Create" },
            ]}
          />
        </div>
        <div>
          <div className="container_ab">
            <Collapse defaultActiveKey="1" expandIconPosition="end">
              <Panel header="Index Creation" key="1">
                {fields.map((elem, index) => {
                  return (
                    <div key={elem.id}>
                      <Row gutter={[8, 8]}>
                        <Col
                          xs={{ span: 12 }}
                          sm={{ span: 4 }}
                          md={{ span: 4 }}
                          lg={{ span: 3 }}
                        >
                          <Form.Item
                            label="Field Name"
                            labelCol={{ span: 24 }}
                            required={false}
                          >
                            <Controller
                              control={control}
                              name={`formData.${index}.name`}
                              render={({ field }) => (
                                <Input
                                  {...field}
                                  style={{ width: "100%" }}
                                  status={
                                    errors?.formData?.[index]?.name
                                      ? "error"
                                      : ""
                                  }
                                  placeholder="Field Name"
                                />
                              )}
                            />
                          </Form.Item>
                        </Col>
                        <Col
                          xs={{ span: 12 }}
                          sm={{ span: 4 }}
                          md={{ span: 4 }}
                          lg={{ span: 3 }}
                        >
                          <Form.Item label="Field Type" labelCol={{ span: 24 }}>
                            <Controller
                              control={control}
                              name={`formData.${index}.type`}
                              render={({
                                field: { value, onChange, onBlur },
                              }) => (
                                <Select
                                  showSearch
                                  value={value}
                                  onBlur={onBlur}
                                  onChange={(e) => {
                                    const type = getValues(
                                      `formData.${index}.type`,
                                    );
                                    if (
                                      (type !== "Date" && e === "Date") ||
                                      (type === "Date" && e !== "Date")
                                    ) {
                                      setValue(
                                        `formData.${index}.format`,
                                        e === "Date" ? null : undefined,
                                      );
                                    }
                                    if (e !== "Text") {
                                      setValue(`formData.${index}.wildcard`, 0);
                                    }
                                    onChange(e);
                                  }}
                                  style={{ width: "100%" }}
                                  status={
                                    errors?.formData?.[index]?.type
                                      ? "error"
                                      : ""
                                  }
                                  placeholder="Field Type"
                                >
                                  <Option value="Boolean">Boolean</Option>
                                  <Option value="Date">Date</Option>
                                  <Option value="Integer">Number</Option>
                                  <Option value="Double Number">
                                    Double Number
                                  </Option>
                                  <Option value="Text">Text</Option>
                                  <Option value="Long Text">Long Text</Option>
                                </Select>
                              )}
                            />
                          </Form.Item>
                        </Col>
                        <Col
                          xs={{ span: 12 }}
                          sm={{ span: 4 }}
                          md={{ span: 4 }}
                          lg={{ span: 3 }}
                        >
                          <Form.Item
                            style={{ xs: 4, sm: 4 }}
                            label="Format"
                            labelCol={{ span: 24 }}
                            // help={}
                          >
                            <Controller
                              control={control}
                              name={`formData.${index}.format`}
                              render={({ field }) =>
                                watch(`formData.${index}.type`) !== "Date" ? (
                                  <Input
                                    // status={}
                                    {...field}
                                    placeholder="Format"
                                  />
                                ) : (
                                  <Select
                                    style={{ width: "100%" }}
                                    {...field}
                                    placeholder="Format"
                                    status={
                                      errors?.formData?.[index]?.format
                                        ? "error"
                                        : ""
                                    }
                                  >
                                    {dateFormats.map((format) => (
                                      <Option
                                        key={format._id}
                                        value={format.default}
                                      >
                                        {format.default}
                                      </Option>
                                    ))}
                                  </Select>
                                )
                              }
                            />
                          </Form.Item>
                        </Col>
                        <Col
                          xs={{ span: 6 }}
                          sm={{ span: 4 }}
                          md={{ span: 4 }}
                          lg={{ span: 2 }}
                        >
                          <Form.Item label="Wildcard" labelCol={{ span: 24 }}>
                            <Controller
                              control={control}
                              name={`formData.${index}.wildcard`}
                              render={({ field }) => (
                                <InputNumber
                                  disabled={
                                    watch(`formData.${index}.type`) !== "Text"
                                  }
                                  style={{ width: "100%" }}
                                  min={0}
                                  precision={0}
                                  step={1}
                                  {...field}
                                  placeholder="Wildcard"
                                />
                              )}
                            />
                          </Form.Item>
                        </Col>
                        <Col
                          xs={{ span: 6 }}
                          sm={{ span: 4 }}
                          md={{ span: 4 }}
                          lg={{ span: 2 }}
                        >
                          <Form.Item label="Order" labelCol={{ span: 24 }}>
                            <Controller
                              control={control}
                              name={`formData.${index}.order`}
                              render={({ field }) => (
                                <InputNumber
                                  style={{ width: "100%" }}
                                  min={0}
                                  precision={0}
                                  step={1}
                                  {...field}
                                  placeholder="Order"
                                />
                              )}
                            />
                          </Form.Item>
                        </Col>
                        <Col
                          xs={{ span: 12 }}
                          sm={{ span: 4 }}
                          md={{ span: 4 }}
                          lg={{ span: 2 }}
                        >
                          <Form.Item label="Required" labelCol={{ span: 24 }}>
                            <Controller
                              control={control}
                              name={`formData.${index}.required`}
                              render={({ field }) => (
                                <Select {...field}>
                                  <Option value>True</Option>
                                  <Option value={false}>False</Option>
                                </Select>
                              )}
                            />
                          </Form.Item>
                        </Col>
                        <Col
                          xs={{ span: 12 }}
                          sm={{ span: 4 }}
                          md={{ span: 4 }}
                          lg={{ span: 2 }}
                        >
                          <Form.Item label="Editable" labelCol={{ span: 24 }}>
                            <Controller
                              control={control}
                              name={`formData.${index}.editable`}
                              render={({ field }) => (
                                <Select {...field}>
                                  <Option value>True</Option>
                                  <Option value={false}>False</Option>
                                </Select>
                              )}
                            />
                          </Form.Item>
                        </Col>
                        <Col
                          xs={{ span: 12 }}
                          sm={{ span: 4 }}
                          md={{ span: 4 }}
                          lg={{ span: 3 }}
                        >
                          <Form.Item
                            label="Default Index"
                            labelCol={{ span: 24 }}
                          >
                            <Controller
                              control={control}
                              name={`formData.${index}.defaultIndex`}
                              render={({ field }) => (
                                <Select {...field}>
                                  <Option value>True</Option>
                                  <Option value={false}>False</Option>
                                </Select>
                              )}
                            />
                          </Form.Item>
                        </Col>
                        <Col
                          xs={{ span: 11 }}
                          sm={{ span: 4 }}
                          md={{ span: 4 }}
                          lg={{ span: 3 }}
                        >
                          <Form.Item
                            label="Available In IS"
                            labelCol={{ span: 24 }}
                            style={{ textOverflow: "ellipsis" }}
                          >
                            <Controller
                              control={control}
                              name={`formData.${index}.availableInIS`}
                              render={({ field }) => (
                                <Select {...field}>
                                  <Option value>True</Option>
                                  <Option value={false}>False</Option>
                                </Select>
                              )}
                            />
                          </Form.Item>
                        </Col>
                        <Col
                          xs={{ span: 11 }}
                          sm={{ span: 4 }}
                          md={{ span: 4 }}
                          lg={{ span: 3 }}
                        >
                          <Form.Item
                            label="Tags"
                            labelCol={{ span: 24 }}
                            style={{ textOverflow: "ellipsis" }}
                          >
                            <Controller
                              control={control}
                              name={`formData.${index}.tags`}
                              render={({ field }) => (
                                <Select
                                  mode="tags"
                                  showArrow
                                  tagRender={IndexTagRenderer}
                                  {...field}
                                  style={{ width: "100%" }}
                                  options={IndexTagOptions}
                                />
                              )}
                            />
                          </Form.Item>
                        </Col>
                        <Col
                          xs={{ span: 1 }}
                          sm={{ span: 4 }}
                          md={{ span: 1 }}
                          lg={{ span: 1 }}
                        >
                          <Form.Item>
                            {index === fields.length - 1 ? (
                              <PlusCircleOutlined
                                onClick={() =>
                                  append({ ...defaultIndexRecord })
                                }
                              />
                            ) : (
                              <MinusCircleOutlined
                                onClick={() => remove(index)}
                              />
                            )}
                          </Form.Item>
                        </Col>
                      </Row>
                    </div>
                  );
                })}
                <Row gutter={[8, 8]}>
                  <Col
                    xs={{ span: 24 }}
                    sm={{ span: 12, offset: 4 }}
                    md={{ span: 8, offset: 4 }}
                    lg={{ span: 8, offset: 4 }}
                  >
                    <Button
                      type="primary"
                      loading={isLoading}
                      onClick={handleSubmit(createIndexes, errorHandler)}
                    >
                      Create
                    </Button>
                    <Button onClick={() => reset()}>Cancel</Button>
                  </Col>
                </Row>
              </Panel>
            </Collapse>
          </div>
        </div>
      </Layout>
    </>
  );
  // }
};

IndexTagRenderer.propTypes = {
  label: PropTypes.string.isRequired,
  value: PropTypes.string.isRequired,
  closable: PropTypes.bool.isRequired,
  onClose: PropTypes.func,
};

IndexTagRenderer.defaultProps = {
  onClose: () => {},
};

export default CreateIndexes;
